import { action, computed, makeObservable, observable } from 'mobx';

import { Locale } from 'chaika-enums';

import { getTranslatesForAllLocales } from '@chaika/i18n/helper';
import { store } from '@store';
import { changePositionInMapObject } from '@utils/changePositionInMapObject';
import { toArrayFromIterable } from '@utils/toArrayFromIterable';

import {
    EntryTypeBlockCreateForm,
    EntryTypeBlockData,
    EntryTypeBlockStatus,
    EntryTypeFieldData,
    EntryTypeFieldName,
    EntryTypeFieldType,
    Optional,
} from 'chaika-frontend-types/frontend-cms';
import type { TranslationsDataParams } from 'chaika-types';

import { EntryTypeField } from './EntryTypeField';

import { ENTRY_FIELD_EXCEPTIONS } from './types';
import { MedicationField } from '@store/models/EntryFields';

interface BlockParams extends EntryTypeBlockData {
    sectionTitle?: string;
}

const enabledInViewModeFieldNames: EntryTypeFieldName[] = [
    EntryTypeFieldName.RenderedService,
    EntryTypeFieldName.MaterialAct,
    EntryTypeFieldName.SickLeave,
    EntryTypeFieldName.EntryEpisodes,
    EntryTypeFieldName.EpisdodesDiagnosis,
    EntryTypeFieldName.EpisdodesReferrals,
    EntryTypeFieldName.EpisdodesDrugs,
    EntryTypeFieldName.EpisdodesRecommendations,
    EntryTypeFieldName.EpisdodesTasks,
    EntryTypeFieldName.EpisdodesPlanDescription,
    EntryTypeFieldName.DentalTreatment,
];
const getEnabledInViewModeBlockNames = (): Set<string> => new Set(getTranslatesForAllLocales('dentalFormula'));
const enabledInViewModeBlockIds: Set<string> = new Set([
    '8936fdd0-88b5-4e9e-9974-97628b03e040',
    'c89d6ad3-b9da-4e44-9d7d-5290018e3a9a',
]);

export class EntryTypeBlock {
    @observable public id: string;
    @observable public version: number;
    @observable public status: EntryTypeBlockStatus;
    @observable public sectionTitle: Optional<string>;
    @observable public titleTranslations = new Map<Locale, TranslationsDataParams>();
    @observable public title: string;
    @observable public name: string;
    @observable public alwaysShow: boolean;
    @observable private fields_: Map<string, EntryTypeField> = new Map();

    constructor({
        id,
        title,
        name,
        fields = [],
        version,
        status,
        sectionTitle,
        alwaysShow = false,
        titleTranslations = [],
    }: BlockParams) {
        makeObservable(this);

        this.id = id;
        this.version = version;
        this.status = status;
        this.title = title;
        this.alwaysShow = alwaysShow;
        this.name = name;
        this.sectionTitle = sectionTitle;

        titleTranslations.forEach(title => this.titleTranslations.set(title.locale, title));
        fields.forEach(field => {
            this.fields_.set(field.name, new EntryTypeField(field));
        });
    }

    @computed
    public get hasSystemField(): boolean {
        return this.fields.some(
            filed =>
                filed.type === EntryTypeFieldType.MedicalCases ||
                filed.type === EntryTypeFieldType.Drug ||
                filed.type === EntryTypeFieldType.LaboratoryTests ||
                filed.type === EntryTypeFieldType.Referrals ||
                filed.type === EntryTypeFieldType.Vaccinations,
        );
    }

    @computed
    public get fieldNames(): string[] {
        return Array.from(this.fields_.keys());
    }

    @computed
    public get fields(): EntryTypeField[] {
        return Array.from(this.fields_.values());
    }

    @computed
    public get fieldsCount(): number {
        return this.fields_.size;
    }

    @computed
    public get translationLocales(): string[] {
        return Array.from(this.titleTranslations.keys());
    }

    @computed
    public get titleTranslationsValues(): TranslationsDataParams[] {
        return toArrayFromIterable(this.titleTranslations);
    }

    @computed
    public get fieldsWithData(): EntryTypeField[] {
        const fields: EntryTypeField[] = [];

        const withDataNames = new Set(this.fieldWithDataNames);

        this.fields_.forEach(item => {
            if (withDataNames.has(item.name)) {
                fields.push(item);
            }
        });

        return fields;
    }

    @computed
    public get fieldWithDataNames(): string[] {
        const { entry } = store;
        const names: string[] = [];

        this.fields_.forEach(item => {
            const field = entry.fields.get(item.name);
            if ((field && field.hasData) || ENTRY_FIELD_EXCEPTIONS.includes(item.name as EntryTypeFieldType)) {
                names.push(item.name);
            }
        });

        return names;
    }

    @computed
    public get hasData(): boolean {
        return this.calculateHasData(false);
    }

    @computed
    public get hasSpecialActiveData(): boolean {
        return this.calculateHasDataSpecialFields(false);
    }

    @computed
    public get hasDataWithEhr(): boolean {
        return this.calculateHasData(true);
    }

    @computed
    public get isFilled(): boolean {
        const { entry } = store;
        return this.fields.every(item => {
            const field = entry.fields.get(item.name);
            return field && field.hasData;
        });
    }

    @computed
    public get saveParams(): EntryTypeBlockCreateForm {
        return {
            title: this.title,
            name: this.name,
            titleTranslations: this.titleTranslationsValues,
            alwaysShow: this.alwaysShow,
            fields: this.fields.map(field => field.saveParams),
        };
    }

    @computed
    public get isEnableOnViewMode(): boolean {
        return (
            getEnabledInViewModeBlockNames().has(this.name) ||
            enabledInViewModeBlockIds.has(this.id) ||
            enabledInViewModeFieldNames.some(name => this.hasField(name))
        );
    }

    public getField(name: string): Optional<EntryTypeField> {
        return this.fields_.get(name);
    }

    public hasField(name: string): boolean {
        return this.fields_.has(name);
    }

    public generateNewFieldName(): string {
        return `field ${this.makeFieldName(5)}`;
    }

    @action
    public changeFieldPosition(oldPosition: number, newPosition: number): void {
        this.fields_ = changePositionInMapObject(this.fields_, oldPosition, newPosition);
    }

    public getFieldPosition(filedName: string): number {
        return this.fieldNames.findIndex(name => name === filedName);
    }

    @action
    public addField(params: EntryTypeFieldData): void {
        this.fields_.set(params.name, new EntryTypeField(params));
    }

    @action
    public removeField(fieldName: string): void {
        this.fields_.delete(fieldName);
    }

    private makeFieldName(length: number): string {
        let result = '';
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const charactersLength = characters.length;
        for (let i = 0; i < length; i++) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
        }
        return result;
    }

    private calculateHasData(withEhr: boolean): boolean {
        const { entry } = store;
        return this.fields.some(item => {
            const field = withEhr ? entry.getField(item.name) : entry.fields.get(item.name);
            const isComputed = item.type === EntryTypeFieldType.Computed;
            return isComputed
                ? !!entry.computedFields.get(item.name)
                : (field && field.hasData) || ENTRY_FIELD_EXCEPTIONS.includes(item.name as EntryTypeFieldType);
        });
    }

    private calculateHasDataSpecialFields(withEhr: boolean): boolean {
        const { entry } = store;

        return this.fields.some(item => {
            const field = withEhr ? entry.getField(item.name) : entry.fields.get(item.name);
            const isUsedMedicationField = item.type === EntryTypeFieldType.UsedMedications;

            const isComputed = item.type === EntryTypeFieldType.Computed;
            return isComputed
                ? !!entry.computedFields.get(item.name)
                : (field && isUsedMedicationField ? (field as MedicationField).hasActiveData : field.hasData) ||
                      ENTRY_FIELD_EXCEPTIONS.includes(item.name as EntryTypeFieldType);
        });
    }
}
